
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { discoveryService } from "@/services/discoveryService";
import { favoritesService } from "@/services/favoritesService";
import { supabase } from "@/integrations/supabase/client";

export const useDiscoverySubCategories = () => {
  const { user } = useAuth();

  const { data: newUsers, isLoading: isLoadingNew } = useQuery({
    queryKey: ['discovery-new-users', user?.id],
    queryFn: () => discoveryService.fetchNewUsers(user?.id || '', 6),
    enabled: !!user
  });

  const { data: mostBuzzzedUsers, isLoading: isLoadingBuzzz } = useQuery({
    queryKey: ['discovery-most-buzzz-users', user?.id],
    queryFn: () => discoveryService.fetchMostBuzzzedUsers(user?.id || '', 6),
    enabled: !!user
  });

  const { data: travelerUsers, isLoading: isLoadingTravelers } = useQuery({
    queryKey: ['discovery-traveler-users', user?.id],
    queryFn: () => discoveryService.fetchTravelerUsers(user?.id || '', 6),
    enabled: !!user
  });

  const { data: favoriteUsers, isLoading: isLoadingFavorites } = useQuery({
    queryKey: ['discovery-favorite-users', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const favorites = await favoritesService.getFavorites(user.id);
      // Transform the nested data structure to match discovery format
      return favorites.slice(0, 6).map((fav: any) => ({
        ...fav.user_profiles,
        distance: 1, // Default distance since favorites don't have location context
        isOnline: false, // We don't have online status in favorites
        last_seen: null
      }));
    },
    enabled: !!user
  });

  const { data: theirFavoriteUsers, isLoading: isLoadingTheirFavorites } = useQuery({
    queryKey: ['discovery-their-favorite-users', user?.id],
    queryFn: () => favoritesService.getUsersWhoFavoritedMe(user?.id || '', 6),
    enabled: !!user
  });

  const { data: visitorsUsers, isLoading: isLoadingVisitors } = useQuery({
    queryKey: ['discovery-visitors-users', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      // Load visitors with view count (people who viewed my profile)
      const { data: visitorsData } = await supabase
        .from('profile_views')
        .select('viewer_id, viewed_at')
        .eq('viewed_id', user.id)
        .order('viewed_at', { ascending: false });

      // Group by viewer_id and count views
      const viewerCounts: { [key: string]: { count: number; lastViewed: string } } = {};
      visitorsData?.forEach(view => {
        if (!viewerCounts[view.viewer_id]) {
          viewerCounts[view.viewer_id] = { count: 0, lastViewed: view.viewed_at };
        }
        viewerCounts[view.viewer_id].count++;
        // Keep the most recent view date
        if (view.viewed_at > viewerCounts[view.viewer_id].lastViewed) {
          viewerCounts[view.viewer_id].lastViewed = view.viewed_at;
        }
      });

      const uniqueViewerIds = Object.keys(viewerCounts).slice(0, 6); // Limit to 6 for preview
      if (uniqueViewerIds.length === 0) return [];

      const { data: visitorProfiles } = await supabase
        .from('user_profiles')
        .select('user_id, display_name, photo_urls, location')
        .in('user_id', uniqueViewerIds);

      return uniqueViewerIds.map(viewerId => {
        const profile = visitorProfiles?.find(p => p.user_id === viewerId);
        const viewData = viewerCounts[viewerId];
        return {
          user_id: viewerId,
          display_name: profile?.display_name || 'Anonymous',
          photo_urls: profile?.photo_urls || [],
          location: profile?.location || '',
          distance: Math.floor(Math.random() * 10) + 1,
          isOnline: false,
          last_seen: viewData.lastViewed,
          view_count: viewData.count
        };
      });
    },
    enabled: !!user
  });

  return {
    newUsers: newUsers || [],
    mostBuzzzedUsers: mostBuzzzedUsers || [],
    travelerUsers: travelerUsers || [],
    favoriteUsers: favoriteUsers || [],
    theirFavoriteUsers: theirFavoriteUsers || [],
    visitorsUsers: visitorsUsers || [],
    isLoadingNew,
    isLoadingBuzzz,
    isLoadingTravelers,
    isLoadingFavorites,
    isLoadingTheirFavorites,
    isLoadingVisitors
  };
};
